/**
 * Hook for detecting potential duplicate patients during form entry.
 *
 * Watches first name, last name, and date of birth fields, then searches
 * for existing patients that match. Uses debounced search and client-side
 * similarity scoring to avoid noisy early matches while still catching
 * likely misspellings.
 */

// Hooks
import { useState, useEffect, useRef } from "react";
import { useMedplum } from "@medplum/react";
import { useDebounce } from "@benchmark/ui/hooks";

// Types
import type { Patient } from "@medplum/fhirtypes";

/**
 * A potential duplicate patient match from search.
 * @agentic @benchmark/core
 */
export type DuplicatePatientMatch = {
  patientId: string;
  displayName: string;
  dateOfBirth?: string;
};

/**
 * Result type for usePatientDuplicateCheck hook.
 * @agentic @benchmark/core
 */
export type UsePatientDuplicateCheckResult = {
  duplicates: DuplicatePatientMatch[];
  isChecking: boolean;
};

/**
 * Options for usePatientDuplicateCheck hook behavior.
 * @agentic @benchmark/core
 */
export type UsePatientDuplicateCheckOptions = {
  debounce?: boolean;
};
/** Debounce delay for duplicate check search in ms. @agentic connections */
const DEBOUNCE_MS = 1100;
/** Minimum first/last name length to trigger search. @agentic connections */
const MIN_NAME_LENGTH = 2;
const MIN_NAME_LENGTH_WITHOUT_DOB = 4;
const SEARCH_PREFIX_LENGTH = 4;
const SEARCH_RESULT_LIMIT = 20;
const DISPLAY_RESULT_LIMIT = 5;
const DOB_MATCH_MIN_SIMILARITY = 0.72;
const DOB_MISMATCH_MIN_SIMILARITY = 0.96;
const NO_DOB_FIRST_NAME_MIN_SIMILARITY = 0.9;
const NO_DOB_LAST_NAME_MIN_SIMILARITY = 0.88;

type PatientNameParts = {
  given: string;
  family: string;
};

type ScoredMatch = {
  score: number;
  match: DuplicatePatientMatch;
};

const extractNameParts = (patient: Patient): PatientNameParts => {
  const primaryName =
    patient.name?.find((name) => name.family || name.given?.length) ||
    patient.name?.[0];

  return {
    given: primaryName?.given?.join(" ") || "",
    family: primaryName?.family || "",
  };
};

/**
 * Builds display name from Patient name array.
 * @agentic @benchmark/core
 */
const buildDisplayName = (patient: Patient): string => {
  const { given, family } = extractNameParts(patient);
  return `${given} ${family}`.trim() || patient.id || "Unknown";
};

const normalizeName = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/gi, "")
    .toLowerCase();

const calculateLevenshteinDistance = (
  source: string,
  target: string,
): number => {
  if (source === target) {
    return 0;
  }

  if (source.length === 0) {
    return target.length;
  }

  if (target.length === 0) {
    return source.length;
  }

  const previousRow = Array.from(
    { length: target.length + 1 },
    (_, index) => index,
  );

  for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
    let previousDiagonal = previousRow[0];
    previousRow[0] = sourceIndex;

    for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
      const previousUpper = previousRow[targetIndex];
      const substitutionCost =
        source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;

      previousRow[targetIndex] = Math.min(
        previousUpper + 1,
        previousRow[targetIndex - 1] + 1,
        previousDiagonal + substitutionCost,
      );

      previousDiagonal = previousUpper;
    }
  }

  return previousRow[target.length];
};

const calculateNameSimilarity = (source: string, target: string): number => {
  if (!source && !target) {
    return 1;
  }

  if (!source || !target) {
    return 0;
  }

  const maxLength = Math.max(source.length, target.length);
  const distance = calculateLevenshteinDistance(source, target);
  return (maxLength - distance) / maxLength;
};

const shouldIncludeMatch = ({
  firstNameSimilarity,
  lastNameSimilarity,
  hasInputDob,
  hasMatchingDob,
}: {
  firstNameSimilarity: number;
  lastNameSimilarity: number;
  hasInputDob: boolean;
  hasMatchingDob: boolean;
}): boolean => {
  if (hasInputDob) {
    if (hasMatchingDob) {
      return (
        firstNameSimilarity >= DOB_MATCH_MIN_SIMILARITY &&
        lastNameSimilarity >= DOB_MATCH_MIN_SIMILARITY
      );
    }

    return (
      firstNameSimilarity >= DOB_MISMATCH_MIN_SIMILARITY &&
      lastNameSimilarity >= DOB_MISMATCH_MIN_SIMILARITY
    );
  }

  return (
    firstNameSimilarity >= NO_DOB_FIRST_NAME_MIN_SIMILARITY &&
    lastNameSimilarity >= NO_DOB_LAST_NAME_MIN_SIMILARITY
  );
};

const calculateMatchScore = ({
  firstNameSimilarity,
  lastNameSimilarity,
  hasMatchingDob,
}: {
  firstNameSimilarity: number;
  lastNameSimilarity: number;
  hasMatchingDob: boolean;
}): number => {
  const weightedNameSimilarity =
    firstNameSimilarity * 0.45 + lastNameSimilarity * 0.55;
  return hasMatchingDob ? weightedNameSimilarity + 0.1 : weightedNameSimilarity;
};

/**
 * Debounced search for duplicate patients by name and DOB; excludes existingPatientId.
 * @agentic @benchmark/core
 * Checks for existing patients matching the provided name and DOB.
 *
 * The search triggers once first and last names are meaningful enough:
 * both names require at least {@link MIN_NAME_LENGTH} characters, and if
 * DOB is still empty we additionally wait until each name has at least
 * {@link MIN_NAME_LENGTH_WITHOUT_DOB} characters.
 *
 * @param firstName - Current first name field value
 * @param lastName - Current last name field value
 * @param dob - Current date of birth field value (YYYY-MM-DD), used for scoring
 * and ranking rather than server-side filtering
 * @param existingPatientId - If editing, the current patient's ID to exclude from results
 * @param options - Optional behavior controls (e.g. debounce on/off)
 */
export const usePatientDuplicateCheck = (
  firstName: string,
  lastName: string,
  dob: string,
  existingPatientId?: string,
  options: UsePatientDuplicateCheckOptions = {},
): UsePatientDuplicateCheckResult => {
  const { debounce = true } = options;
  const medplum = useMedplum();
  const [duplicates, setDuplicates] = useState<DuplicatePatientMatch[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const rawFirstName = firstName.trim();
  const rawLastName = lastName.trim();
  const rawDob = dob.trim();

  const debouncedFirstName = useDebounce(rawFirstName, DEBOUNCE_MS);
  const debouncedLastName = useDebounce(rawLastName, DEBOUNCE_MS);
  const debouncedDob = useDebounce(rawDob, DEBOUNCE_MS);

  const effectiveFirstName = debounce ? debouncedFirstName : rawFirstName;
  const effectiveLastName = debounce ? debouncedLastName : rawLastName;
  const effectiveDob = debounce ? debouncedDob : rawDob;

  const hasDob = effectiveDob.length > 0;
  const hasMinInput =
    effectiveFirstName.length >= MIN_NAME_LENGTH &&
    effectiveLastName.length >= MIN_NAME_LENGTH &&
    (hasDob ||
      (effectiveFirstName.length >= MIN_NAME_LENGTH_WITHOUT_DOB &&
        effectiveLastName.length >= MIN_NAME_LENGTH_WITHOUT_DOB));

  useEffect(() => {
    if (!hasMinInput) {
      queueMicrotask(() => {
        abortRef.current?.abort();
        setDuplicates([]);
        setIsChecking(false);
      });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const search = async () => {
      setIsChecking(true);
      try {
        const normalizedInputFirstName = normalizeName(effectiveFirstName);
        const normalizedInputLastName = normalizeName(effectiveLastName);
        const searchParams: Record<string, string> = {
          given: effectiveFirstName.slice(0, SEARCH_PREFIX_LENGTH),
          family: effectiveLastName.slice(0, SEARCH_PREFIX_LENGTH),
          _count: SEARCH_RESULT_LIMIT.toString(),
        };
        // Keep DOB out of the server query so we can still score near-name
        // candidates with mismatched DOBs (e.g. data-entry typos).

        const results = await medplum.searchResources("Patient", searchParams);

        if (controller.signal.aborted) return;

        const matches: DuplicatePatientMatch[] = (results || [])
          .map((patient: Patient): ScoredMatch | null => {
            if (!patient.id || patient.id === existingPatientId) {
              return null;
            }

            const { given, family } = extractNameParts(patient);
            const firstNameSimilarity = calculateNameSimilarity(
              normalizedInputFirstName,
              normalizeName(given),
            );
            const lastNameSimilarity = calculateNameSimilarity(
              normalizedInputLastName,
              normalizeName(family),
            );
            const hasMatchingDob =
              Boolean(effectiveDob) && patient.birthDate === effectiveDob;

            if (
              !shouldIncludeMatch({
                firstNameSimilarity,
                lastNameSimilarity,
                hasInputDob: hasDob,
                hasMatchingDob,
              })
            ) {
              return null;
            }

            return {
              score: calculateMatchScore({
                firstNameSimilarity,
                lastNameSimilarity,
                hasMatchingDob,
              }),
              match: {
                patientId: patient.id,
                displayName: buildDisplayName(patient),
                dateOfBirth: patient.birthDate,
              },
            };
          })
          .filter((result): result is ScoredMatch => result !== null)
          .sort((left, right) => right.score - left.score)
          .slice(0, DISPLAY_RESULT_LIMIT)
          .map((result) => result.match);

        setDuplicates(matches);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Duplicate patient check failed:", err);
          setDuplicates([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsChecking(false);
        }
      }
    };

    search();

    return () => controller.abort();
  }, [
    medplum,
    debounce,
    effectiveFirstName,
    effectiveLastName,
    effectiveDob,
    hasDob,
    hasMinInput,
    existingPatientId,
  ]);

  return { duplicates, isChecking };
};
